/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';

// Match Coast's existing palette
const surface = '#fafaf8';
const surfaceHi = '#f5f0e8';
const border = '#d4c8b4';
const gold = '#b8956a';
const text = '#0d3d5c';
const textSec = '#12526e';
const textMut = '#4a6070';
const green = '#1A7A4A';
const fHead = "'Fraunces', Georgia, serif";
const fBody = "'EB Garamond', Georgia, serif";

interface Msa { name: string; state: string; aliases?: string[]; }
interface Focus { property_type: string; msas: Msa[]; }

interface BriefArticle {
  id: number;
  cik: string;
  firm_name: string;
  title: string;
  url: string | null;
  source: string | null;
  published_at: string | null;
  summary: string | null;
  relevance: string | null;
  deal_size_usd: number | null;
  property_type: string | null;
  market: string | null;
  matched_metro: string | null;
}

interface BriefResponse {
  focus: Focus | null;
  articles: BriefArticle[];
  by_metro: { metro: string; count: number }[];
  matched: number;
  window_days: number;
}

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays <= 7) return `${diffDays}d ago`;
  if (diffDays <= 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmt$(n: number | null | undefined): string {
  if (!n) return '';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toLocaleString()}`;
}

interface Props { token: string; onOpenLibrary?: () => void; }

export default function DailyBrief({ token, onOpenLibrary }: Props) {
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState<BriefResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return window.localStorage.getItem('dailyBrief.collapsed') === 'true';
  });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/me/brief?limit=12&days=14`, { headers: auth })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [auth]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    window.localStorage.setItem('dailyBrief.collapsed', String(next));
  };

  // Don't render anything if no focus is set for this user
  if (loading) return null;
  if (!data || !data.focus) return null;

  const focus = data.focus;
  const headerLabel = `${focus.property_type[0].toUpperCase() + focus.property_type.slice(1)} · ${focus.msas.length} markets`;

  return (
    <div style={{
      background: surfaceHi,
      border: `1px solid ${border}`,
      borderLeft: `3px solid ${gold}`,
      margin: '0 0 24px 0',
    }}>
      {/* Header */}
      <div
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '12px 18px', cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${border}`,
        }}
      >
        <span style={{
          color: gold, fontFamily: fBody, fontSize: 9, fontWeight: 700,
          letterSpacing: '0.22em', textTransform: 'uppercase',
        }}>
          Your Brief
        </span>
        <span style={{ color: textSec, fontFamily: fBody, fontSize: 12 }}>
          {headerLabel}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{
          color: text, fontFamily: fHead, fontSize: 14, fontWeight: 700,
        }}>
          {data.matched} {data.matched === 1 ? 'item' : 'items'}
        </span>
        <span style={{
          color: textMut, fontFamily: fBody, fontSize: 11,
        }}>
          last {data.window_days}d
        </span>
        {onOpenLibrary && (
          <button
            onClick={(e) => { e.stopPropagation(); onOpenLibrary(); }}
            style={{
              background: 'transparent',
              border: `1px solid ${gold}`,
              color: gold, fontFamily: fBody, fontSize: 10, fontWeight: 700,
              letterSpacing: '0.16em', textTransform: 'uppercase',
              padding: '4px 10px', cursor: 'pointer',
            }}>
            Library
          </button>
        )}
        <span style={{
          color: textMut, fontFamily: fBody, fontSize: 12,
          transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
          transition: 'transform 0.15s', display: 'inline-block', width: 12,
        }}>
          ▶
        </span>
      </div>

      {!collapsed && (
        <div style={{ padding: '12px 18px 14px' }}>
          {/* Per-metro chips */}
          {data.by_metro.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12,
            }}>
              {focus.msas.map(m => {
                const hit = data.by_metro.find(x => x.metro === m.name);
                const count = hit?.count || 0;
                return (
                  <span key={m.name} style={{
                    background: count > 0 ? '#fff' : 'transparent',
                    border: `1px solid ${count > 0 ? gold + '99' : border}`,
                    color: count > 0 ? text : textMut,
                    fontFamily: fBody, fontSize: 11,
                    padding: '3px 9px',
                    fontWeight: count > 0 ? 600 : 400,
                  }}>
                    {m.name}{count > 0 ? ` · ${count}` : ''}
                  </span>
                );
              })}
            </div>
          )}

          {data.articles.length === 0 ? (
            <div style={{
              color: textMut, fontFamily: fBody, fontSize: 12,
              padding: '12px 0', fontStyle: 'italic',
            }}>
              No matching news in the last {data.window_days} days. Strict filter:
              property type {focus.property_type}, your {focus.msas.length} markets.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.articles.map(a => (
                <a
                  key={a.id}
                  href={a.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '70px 90px 1fr auto',
                    gap: 12, alignItems: 'baseline',
                    padding: '6px 10px',
                    border: `1px solid ${border}`,
                    background: '#fff',
                    textDecoration: 'none',
                  }}
                >
                  <span style={{
                    color: textMut, fontFamily: fBody, fontSize: 11,
                  }}>
                    {fmtDate(a.published_at)}
                  </span>
                  <span style={{
                    color: gold, fontFamily: fBody, fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                  }}>
                    {a.matched_metro || ''}
                  </span>
                  <span style={{
                    color: text, fontFamily: fBody, fontSize: 13,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    <strong style={{ color: text, fontFamily: fHead }}>
                      {a.firm_name}
                    </strong>{' '}
                    <span style={{ color: textSec }}>· {a.title}</span>
                  </span>
                  <span style={{
                    display: 'flex', gap: 10, color: textMut,
                    fontFamily: fBody, fontSize: 11, whiteSpace: 'nowrap',
                  }}>
                    {a.deal_size_usd && (
                      <span style={{ color: green, fontWeight: 700 }}>
                        {fmt$(a.deal_size_usd)}
                      </span>
                    )}
                    {a.source && (
                      <span style={{
                        color: textMut, letterSpacing: '0.06em',
                        textTransform: 'uppercase', fontSize: 9,
                      }}>
                        {a.source}
                      </span>
                    )}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
